import {
  FormControl,
  Grid2 as Grid,
  InputLabel,
  MenuItem,
  PlatformDialog,
  Select,
  TextField,
  pxToRem,
  Typography,
  AutocompleteSearchBox,
  Box,
  FormControlLabel,
  Checkbox,
  SelectChangeEvent,
  Alert,
} from "@platform/style";
import React, { useState } from "react";
import { ActionEnum, ResolutionReason } from "../models/ReasonsAndResolutions";
import { fetchWithToken } from "@platform/core";
import { WORK_MANAGEMENT_ADMIN_BFF_API_BASE } from "../platform-workitems";
import { reportError } from "../utils/errors";
import { fetchUsers } from "./workItems/apiClient";
import { format } from "date-fns";

interface MassActionPopUpDialogBoxProps {
  typeOfMassAction: string;
  title: string;
  selectedRowCount: number;
  open: boolean;
  onClose: () => void;
  onSubmit: (
    resolutionCode: string | undefined,
    comment: string | undefined,
    assignToWorker: string | undefined
  ) => void;
}

export const MassActionPopUpDialogBox = ({
  typeOfMassAction,
  title,
  selectedRowCount,
  open,
  onClose,
  onSubmit,
}: MassActionPopUpDialogBoxProps) => {
  const [resolutionReasonList, setResolutionReasonList] = React.useState<ResolutionReason[]>([]);
  const [comment, setComment] = React.useState<string | undefined>(undefined);
  const [oktaUsers, setOktaUsers] = useState<{ key: string; name: string; sentence: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOption, setSelectedOption] = useState<any>(null);
  const [offset, setOffset] = useState(0);
  const [fieldName, setFieldName] = useState("AssignWorkItemToWorker");
  const [checked, setChecked] = React.useState(false);
  const [selectedReasonCode, setSelectedReasonCode] = React.useState<string | undefined>(undefined);
  const [fetchUsersError, setFetchUsersError] = React.useState<string | null>(null);
  const maxLength = 500;

  React.useEffect(() => {
    switch (typeOfMassAction) {
      case ActionEnum.CANCEL:
      case ActionEnum.COMPLETE:
        (async () => {
          await fetchWithToken(
            `${WORK_MANAGEMENT_ADMIN_BFF_API_BASE}/resolution-reasons?action=${typeOfMassAction}`,
            {
              method: "GET",
            }
          )
            .then((response) => {
              if (!response.ok) {
                throw new Error(JSON.stringify(response));
              }
              return response.json();
            })
            .then((result) => {
              setResolutionReasonList(result);
            })
            .catch((error) => {
              reportError(error.message);
            });
        })();
    }
  }, [typeOfMassAction]);

  React.useEffect(() => {
    let delayFunctionCall: string | number | NodeJS.Timeout | undefined;
    if (searchTerm?.length >= 2) {
      delayFunctionCall = setTimeout(() => {
        fetchUsers(searchTerm, setLoading, setOktaUsers, setFetchUsersError).then();
      }, 700);
    }
    return () => clearTimeout(delayFunctionCall);
  }, [searchTerm]);

  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setComment(value.trim() === "" ? undefined : value);
  };

  const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  const handleItemChange = (event: SelectChangeEvent) => {
    setSelectedReasonCode(event.target.value);
  };

  const MenuProps = {
    PaperProps: {
      sx: {
        width: "440px",
        p: 2,
        borderRadius: pxToRem(4),
        overflowY: "auto",
        maxHeight: "calc(100vh - 540px)",
      },
    },
  };

  const closeFetchUsersErrorAlert = () => {
    setFetchUsersError(null);
  };

  return (
    <PlatformDialog
      destructive={typeOfMassAction == ActionEnum.CANCEL}
      title={title}
      maxWidth={490}
      open={open}
      onClose={() => {
        setSelectedReasonCode(undefined);
        setComment(undefined);
        setChecked(false);
        onClose();
      }}
      closeLabel={"GO BACK"}
      isSubmitDisabled={
        (typeOfMassAction == ActionEnum.ASSIGN && !selectedOption) ||
        (typeOfMassAction !== ActionEnum.ASSIGN && (!selectedReasonCode || !checked))
      }
      onSubmit={() => {
        setSelectedReasonCode(undefined);
        setComment(undefined);
        setChecked(false);
        onSubmit(
          selectedReasonCode,
          comment || undefined,
          typeOfMassAction === ActionEnum.ASSIGN ? selectedOption.key : selectedOption
        );
      }}
      submitLabel={`${typeOfMassAction} WORK`}
    >
      <Grid container direction="column" justifyContent="space-around" alignItems="center">
        <Typography sx={{ ml: "-215px", mt: "20px", mb: "19px" }}>
          Work items selected to {typeOfMassAction.toLowerCase()}: {selectedRowCount}
        </Typography>

        {typeOfMassAction !== ActionEnum.ASSIGN ? (
          <Grid sx={{ mb: "25px" }}>
            <FormControl required fullWidth={true} size="small">
              <InputLabel id="label-select-reason" sx={{ color: "silver" }}>
                Reason Code
              </InputLabel>
              <Select
                variant="outlined"
                labelId="label-select-reason"
                id="label-select-reason-required"
                value={selectedReasonCode}
                label="Reason Code"
                MenuProps={MenuProps}
                size={"small"}
                sx={{ textAlign: "left", width: "440px" }}
                onChange={handleItemChange}
              >
                {resolutionReasonList.map((reason, index) => (
                  <MenuItem
                    key={index}
                    sx={{ whiteSpace: "normal", wordWrap: "break-all" }}
                    value={reason.ResolutionReasonCode}
                  >
                    {reason.ResolutionReason}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        ) : (
          <Grid sx={{ mb: fetchUsersError ? "85px" : "25px", width: "440px", height: "45px" }}>
            {fetchUsersError && (
              <Alert
                onClose={closeFetchUsersErrorAlert}
                severity="error"
                sx={{
                  width: "calc(100% - 3px)",
                  borderColor: "error.main",
                  borderWidth: "1px",
                  borderStyle: "solid",
                  mb: 2,
                }}
              >
                {fetchUsersError}
              </Alert>
            )}
            <FormControl required fullWidth={true}>
              <AutocompleteSearchBox
                aria-describedby="AssignWorkItemToWorker"
                id="AssignWork"
                name="AssignWorkToWorker"
                placeholder="People Search"
                options={oktaUsers}
                selectedOption={selectedOption}
                setSelectedOption={setSelectedOption}
                setFieldName={setFieldName}
                setOffset={setOffset}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                loading={loading}
                selectionConstrained={true}
                autoHighlight={true}
                autoSelect={true}
                autoComplete={true}
              />
            </FormControl>
          </Grid>
        )}

        <Grid
          sx={{
            mb: "5px",
            size: "small",
          }}
        >
          <InputLabel id="comment-label-id">
            Comment <span style={{ marginLeft: "285px" }}>{format(new Date(), "MM/dd/yyyy")}</span>
          </InputLabel>
          <TextField
            id="comment-textfield-id"
            multiline
            rows={2}
            slotProps={{
              htmlInput: {
                maxLength: maxLength,
              },
              formHelperText: {
                sx: {
                  display: "flex",
                  justifyContent: "flex-end",
                  fontSize: "13px",
                },
              },
            }}
            sx={{
              width: "440px",
              mb: "22px",
            }}
            placeholder={"Add comment"}
            helperText={
              comment ? `Character Count: ${maxLength - comment.length}` : `Character Count: 500`
            }
            onChange={handleCommentChange}
            value={comment}
            name="comment-textfield-name"
          />
        </Grid>
        {typeOfMassAction !== ActionEnum.ASSIGN && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
              p: 2,
              border: "1px solid silver",
              width: "440px",
              backgroundColor: "#eeeeee",
              height: "50px",
              mb: "20px",
            }}
          >
            <FormControlLabel
              control={<Checkbox checked={checked} onChange={handleCheckChange} />}
              label="I confirm understand this action cannot be undone."
            />
          </Box>
        )}
      </Grid>
    </PlatformDialog>
  );
};
